import { CommandLoading } from 'cmdk';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetParentByStudio } from '~entities/parent/api/get-parent-by-studio';

import useDebounce from '~shared/hooks/useDebounce';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~shared/shadcn/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

import { StudentCreationForm } from './student-registration-from';

interface AddStudentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select this parent (family hub Add student). */
  defaultParentId?: string;
  /** When true with defaultParentId, skip the parent picker. */
  lockParent?: boolean;
  /** Shown when lockParent skips the studio parent search. */
  lockedParentLabel?: string;
  onCreated?: () => void;
}

export const AddStudentDialog = ({
  open,
  onOpenChange,
  defaultParentId,
  lockParent = false,
  lockedParentLabel,
  onCreated,
}: AddStudentDialogProps) => {
  const [selectedParentId, setSelectedParentId] = useState(
    defaultParentId ?? '',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [openCombobox, setOpenCombobox] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (!open) return;
    setSelectedParentId(defaultParentId ?? '');
    setSearchQuery('');
    setOpenCombobox(false);
  }, [open, defaultParentId]);

  const { data: parentsData, isFetching: isFetchingParents } =
    useGetParentByStudio({
      page: 1,
      limit: 100,
      search: debouncedSearchQuery,
    });

  const parents = parentsData?.data ?? [];
  const selectedParent = parents.find(
    (parent) => parent._id === selectedParentId,
  );
  const selectedLabel =
    lockedParentLabel?.trim() ||
    (selectedParent
      ? `${selectedParent.firstName} ${selectedParent.lastName}`
      : '');

  const handleCreated = () => {
    setSelectedParentId(defaultParentId ?? '');
    setSearchQuery('');
    setOpenCombobox(false);
    toast.success('Student created successfully');
    onCreated?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto'>
        <DialogHeader className='pb-2'>
          <DialogTitle>Add New Student</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          {!selectedParentId ? (
            <div className='space-y-4'>
              <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    role='combobox'
                    aria-expanded={openCombobox}
                    className='w-full justify-between'
                  >
                    Select parent...
                    <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-full p-0'
                  align='start'
                  side='bottom'
                  sideOffset={4}
                >
                  <Command
                    filter={(value, search) => {
                      const parentName = parents
                        .find((parent) => parent._id === value)
                        ?.name.toLowerCase();
                      return parentName?.includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput
                      placeholder='Search parents...'
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      {isFetchingParents ? (
                        <CommandLoading className='flex w-full items-center justify-center py-2'>
                          <Loader2 className='size-6 animate-spin' />
                        </CommandLoading>
                      ) : (
                        <>
                          <CommandEmpty>No parent found.</CommandEmpty>
                          <CommandGroup>
                            {parents.map((parent) => (
                              <CommandItem
                                key={parent._id}
                                value={parent._id}
                                onSelect={(currentValue) => {
                                  setSelectedParentId(currentValue);
                                  setOpenCombobox(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 size-4',
                                    selectedParentId === parent._id
                                      ? 'opacity-100'
                                      : 'opacity-0',
                                  )}
                                />
                                {parent.firstName} {parent.lastName} -{' '}
                                {parent.email}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <>
              <div className='mb-4 flex items-center justify-between'>
                <div className='text-sm text-muted-foreground'>
                  Selected Parent: {selectedLabel || 'Parent'}
                </div>
                {!lockParent ? (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setSelectedParentId('');
                    }}
                  >
                    Change Parent
                  </Button>
                ) : null}
              </div>
              <StudentCreationForm
                parentId={selectedParentId}
                onSuccess={handleCreated}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
